import { withBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import { withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { formatHeaderValue } from "@components/readOnlyList/ReadOnlyList";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import {
    enhanceHeaderDataWithSmartFilter,
    getDataForReadOnlyListSmartFilter,
    prepareReadOnlyListSmartFilterProps
} from "@components/smart/smartValueHelper/ValueHelper.utils";
import { TId } from "@components/table";
import {
    BankTransactionEntity,
    DocumentEntity,
    InvoiceIssuedEntity,
    InvoiceReceivedEntity
} from "@odata/GeneratedEntityTypes";
import { BankTransactionTypeCode } from "@odata/GeneratedEnums";
import { generalBankAccountFormatter } from "@utils/BankUtils";
import i18next from "i18next";

import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { ValueType } from "../../../enums";
import { IAfterSaveEventArgs } from "../../../model/Model";
import DateType from "../../../types/Date";
import { PairTableViewForExtend } from "../PairTableView";
import {
    canRowBePaired,
    createDefaultPairFilter,
    getBankTransactionType,
    IPairTableViewBaseProps
} from "./BankTransactions.utils";

class BankTransactionPairTableView extends PairTableViewForExtend {
    constructor(props: IPairTableViewBaseProps) {
        super(props);

        this.presetDefaultFilters = this.presetDefaultFilters.bind(this);
        this.presetDefaultFilters();
    }

    get transactionType() {
        return this.props.rootStorage.data.entity.BankTransactionType.Code;
    }

    getTransactionType = () => {
        return getBankTransactionType(this.props.rootStorage);
    };

    get successSubtitle(): string {
        return i18next.t("Banks:Pairing.Saved");
    }

    presetDefaultFilters() {
        createDefaultPairFilter(this.props.storage, this.transactionType);
        this.prepareReadOnlyListSmartFilterProps();
        this.props.storage.applyFilters();
    }

    canRowBePaired = (rowId: TId) => {
        return canRowBePaired({
            storage: this.props.storage,
            paymentType: this.transactionType,
            origPairedDocuments: this.origPairedDocuments,
            rowId
        });
    };

    getTitle = () => {
        return i18next.t(`Banks:Pairing.${this.transactionType === BankTransactionTypeCode.IncomingPayment ? "InPayment" : "OutPayment"}`);
    };

    get transactionAmountProp() {
        return this.transactionType === BankTransactionTypeCode.OutgoingPayment
                ? InvoiceReceivedEntity.TransactionAmountToPay
                : InvoiceIssuedEntity.TransactionAmountToReceive;
    }

    prepareReadOnlyListSmartFilterProps() {
        const { entity } = this.props.rootStorage.data;
        const prop = this.transactionAmountProp;
        const amount = Math.abs(entity.TransactionAmount);
        prepareReadOnlyListSmartFilterProps(this.props.storage, [{
            id: prop,
            value: amount,
            filterValue: JSON.stringify({ [prop]: amount, TransactionCurrencyCode: entity.TransactionCurrency?.Code }),
            valueType: ValueType.Number
        }, {
            id: DocumentEntity.SymbolVariable,
            value: entity.SymbolVariable
        }]);
    }

    handleFormAfterSave(args: IAfterSaveEventArgs) {
        super.handleFormAfterSave(args);

        getDataForReadOnlyListSmartFilter.reset();
        this.prepareReadOnlyListSmartFilterProps();
    }

    getHeaderData = (): IReadOnlyListItem[] => {
        const { rootStorage, storage } = this.props;
        const { entity } = rootStorage.data;

        const acc = generalBankAccountFormatter(null, {
            item: entity.BankStatement?.BankAccountBase
        });
        const accName = entity.BankStatement?.BankAccountBase?.Name;

        return enhanceHeaderDataWithSmartFilter(storage, [{
            label: rootStorage.t("Banks:Pairing.DateOfPay"),
            value: formatHeaderValue(DateType.format(entity.DateBankTransaction))
        }, {
            id: this.transactionAmountProp,
            label: rootStorage.t("Banks:Pairing.Amount"),
            value: formatHeaderValue(this.formatCurrency(Math.abs(entity.TransactionAmount || 0)))
        }, {
            id: BankTransactionEntity.SymbolVariable,
            label: rootStorage.t("Banks:Pairing.SymbolVariable"),
            value: formatHeaderValue(entity.SymbolVariable)
        }, {
            label: rootStorage.t("Banks:Pairing.FromAccount"),
            value: formatHeaderValue(acc),
            description: accName ? ` | ${accName}` : ""
        }, {
            label: rootStorage.t("Banks:Pairing.ToAccount"),
            value: formatHeaderValue(generalBankAccountFormatter(null, {
                item: entity.BankAccount
            }))
        }, {
            label: rootStorage.t("Banks:Pairing.Note"),
            value: formatHeaderValue(entity.RemittanceInformation)
        }]);
    };
}

export default withBusyIndicator({ passBusyIndicator: true, isDelayed: true })(withPermissionContext(withConfirmationDialog(BankTransactionPairTableView)));